import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

const ANCHOR_GAP_PX = 8;
const VIEWPORT_MARGIN_PX = 8;
const CARET_EDGE_INSET_PX = 12;

type TooltipPlacement = 'top' | 'bottom';

type TooltipPosition = {
  top: number;
  left: number;
  caretOffset: number;
  placement: TooltipPlacement;
};

const TooltipShell = styled.div<{
  visible: boolean;
  top: number;
  left: number;
  caretOffset: number;
  placement: TooltipPlacement;
  zIndex: number;
}>`
  position: fixed;
  top: ${(props) => props.top}px;
  left: ${(props) => props.left}px;
  background: var(--purple-dark);
  color: var(--white-main);
  padding: var(--xsmall-padding) var(--medium-padding);
  border-radius: var(--xxxsmall-border-radius);
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  line-height: var(--xsmall-line-height);
  text-align: left;
  cursor: default;
  z-index: ${(props) => props.zIndex};
  opacity: ${(props) => (props.visible ? 1 : 0)};

  &::after {
    content: '';
    position: absolute;
    left: ${(props) => props.caretOffset}px;
    transform: translateX(-50%);
    border-style: solid;
    border-color: transparent;

    ${(props) =>
      props.placement === 'bottom'
        ? `
      bottom: 100%;
      border-width: 0 var(--semi-xsmall-padding) var(--xsmall-padding)
        var(--semi-xsmall-padding);
      border-bottom-color: var(--purple-dark);
    `
        : `
      top: 100%;
      border-width: var(--xsmall-padding) var(--semi-xsmall-padding) 0
        var(--semi-xsmall-padding);
      border-top-color: var(--purple-dark);
    `}
  }
`;

export type PlaceholderPreviewTooltipProps = {
  anchorRef: React.RefObject<HTMLElement>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  zIndex?: number;
  children: React.ReactNode;
};

export const PlaceholderPreviewTooltip: React.FC<
  PlaceholderPreviewTooltipProps
> = ({ anchorRef, onMouseEnter, onMouseLeave, zIndex = 1000, children }) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const tooltip = tooltipRef.current;
    if (!anchor || !tooltip) return;

    const anchorRect = anchor.getBoundingClientRect();
    const { width, height } = tooltip.getBoundingClientRect();

    const fitsAbove =
      anchorRect.top - height - ANCHOR_GAP_PX >= VIEWPORT_MARGIN_PX;
    const lowestTop = Math.max(
      VIEWPORT_MARGIN_PX,
      window.innerHeight - height - VIEWPORT_MARGIN_PX,
    );
    const top = Math.min(
      Math.max(
        fitsAbove
          ? anchorRect.top - height - ANCHOR_GAP_PX
          : anchorRect.bottom + ANCHOR_GAP_PX,
        VIEWPORT_MARGIN_PX,
      ),
      lowestTop,
    );

    const anchorCenter = anchorRect.left + anchorRect.width / 2;
    const furthestLeft = Math.max(
      VIEWPORT_MARGIN_PX,
      window.innerWidth - width - VIEWPORT_MARGIN_PX,
    );
    const left = Math.min(
      Math.max(anchorCenter - width / 2, VIEWPORT_MARGIN_PX),
      furthestLeft,
    );

    const caretOffset = Math.min(
      Math.max(anchorCenter - left, CARET_EDGE_INSET_PX),
      Math.max(CARET_EDGE_INSET_PX, width - CARET_EDGE_INSET_PX),
    );

    setPosition({
      top,
      left,
      caretOffset,
      placement: fitsAbove ? 'top' : 'bottom',
    });
  }, [anchorRef]);

  useLayoutEffect(() => {
    const tooltip = tooltipRef.current;
    if (!tooltip) return;

    updatePosition();

    const resizeObserver = new ResizeObserver(updatePosition);
    resizeObserver.observe(tooltip);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [updatePosition]);

  return createPortal(
    <TooltipShell
      ref={tooltipRef}
      visible={!!position}
      top={position?.top ?? 0}
      left={position?.left ?? 0}
      caretOffset={position?.caretOffset ?? 0}
      placement={position?.placement ?? 'top'}
      zIndex={zIndex}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </TooltipShell>,
    document.body,
  );
};
